'use client';

import useSWR, { mutate } from 'swr';
import FilterBuilder from '../utils/filterBuilder';
import { fleetApi } from '../api/axiosClient';
import { useMemo } from 'react';

const getTzParam = () => `?tz=${Math.abs(new Date().getTimezoneOffset() / 60)}`;

const fetcher = async ([url, payload]) => {
  const response = await fleetApi.post(url + getTzParam(), payload);
  return response;
};

export const revalidateCountryList = () => {
  mutate((key) => Array.isArray(key) && key[0] === '/country/search');
};

export const useCountryList = (page, rowsPerPage, searchKey, searchValue, companyId = '') => {
  const start = page * rowsPerPage;
  const payload = useMemo(() => {
    const fb = new FilterBuilder()
      .setPaging(start, rowsPerPage)
      .like(searchKey, searchValue, 'AND')
      .addJoin('company', ['name'])
      .setOrder(['name'], true);
    if (companyId) {
      const idStr = String(companyId);
      if (idStr.includes(',')) {
        fb.in('company_id', idStr);
      } else {
        fb.equal('company_id', idStr);
      }
    } else {
      fb.filterByCompany();
    }
    return fb.build();
  }, [start, rowsPerPage, searchKey, searchValue, companyId]);
  const key = useMemo(() => ['/country/search', JSON.stringify(payload)], [payload]);

  const { data, error, isLoading } = useSWR(key, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false,
    keepPreviousData: true,
    revalidateOnMount: true
  });

  return {
    data: data?.data,
    totalRecord: data?.totalRecord,
    error,
    isLoading
  };
};
export const getCountryById = async (id) => {
  if (id == null) throw new Error('id is required');

  try {
    const fb = new FilterBuilder().setPaging(0, 1).setOrder(['id'], false).equal('id', String(id));

    const payload = fb.build();
    const response = await fleetApi.post(`/country/search${getTzParam()}`, payload);

    const dataArr = response?.data?.data ?? response?.data ?? (Array.isArray(response) ? response : null);

    const result = Array.isArray(dataArr) && dataArr.length > 0 ? dataArr[0] : null;
    return result;
  } catch (error) {
    console.error('Failed to fetch country by ID:', error);
    throw error;
  }
};

export const createCountry = async (formData = {}) => {
  try {
    const body = { ...formData };
    const response = await fleetApi.post(`/country/new${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateCountryList();
    return response;
  } catch (error) {
    console.error('Error creating country:', error);
    throw error;
  }
};

export const updateCountry = async (id, formData = {}) => {
  if (id == null) throw new Error('id is required');
  try {
    const body = { ...formData };
    const response = await fleetApi.put(`/country/edit/${id}${getTzParam()}`, body);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.code >= 403 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateCountryList();
    return response;
  } catch (error) {
    console.error('Error updating country:', error);
    throw error;
  }
};

export const deleteCountry = async (id) => {
  if (id == null) throw new Error('id is required');
  try {
    const response = await fleetApi.delete(`/country/delete/${id}${getTzParam()}`);

    if (response?.data?.status === false || response?.data?.code >= 400 || response?.data?.error) {
      throw { response: { data: response.data } };
    }
    revalidateCountryList();
    return response.data;
  } catch (error) {
    console.error('Error deleting country:', error);
    throw error;
  }
};
